import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMagazines, createMagazine, deleteMagazine, getTemplates, getSubscriberCount } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CenturionFooter from '@/components/CenturionFooter';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, BookOpen, MoreVertical, Trash2, Edit, Eye, Calendar, Check, Layout, Users, LogOut, Home } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [magazines, setMagazines] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [newMagazine, setNewMagazine] = useState({
    title: '',
    description: '',
    template: 'classic',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [magRes, tempRes, subCountRes] = await Promise.all([
        getMagazines(),
        getTemplates(),
        getSubscriberCount(),
      ]);
      setMagazines(magRes.data);
      setTemplates(tempRes.data);
      setSubscriberCount(subCountRes.data.count);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMagazines = async () => {
    try {
      const response = await getMagazines();
      setMagazines(response.data);
    } catch (error) {
      toast.error('Failed to load magazines');
    }
  };

  const handleCreateMagazine = async () => {
    if (!newMagazine.title.trim()) {
      toast.error('Please enter a magazine title');
      return;
    }
    
    setCreating(true);
    try {
      const response = await createMagazine({
        ...newMagazine,
        sections: [
          { id: crypto.randomUUID(), name: 'Cover Story', order: 0 },
          { id: crypto.randomUUID(), name: 'Community News', order: 1 },
          { id: crypto.randomUUID(), name: 'Features', order: 2 },
        ],
      });
      toast.success('Magazine created successfully!');
      setIsCreateOpen(false);
      setCreateStep(1);
      setNewMagazine({ title: '', description: '', template: 'classic' });
      navigate(`/admin/editor/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create magazine');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreate = () => {
    setCreateStep(1);
    setNewMagazine({ title: '', description: '', template: 'classic' });
    setIsCreateOpen(true);
  };

  const handleDeleteMagazine = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this magazine?')) return;
    
    try {
      await deleteMagazine(id);
      toast.success('Magazine deleted');
      fetchMagazines();
    } catch (error) {
      toast.error('Failed to delete magazine');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="creative-bg">
      <div className="wave-decoration" />
      {/* Floating decorative shapes - Bahamian colors */}
      <div className="floating-shape floating-shape-1" />
      <div className="floating-shape floating-shape-2" />
      <div className="floating-shape floating-shape-3" />
      <div className="floating-shape floating-shape-4" />
      <div className="floating-shape floating-shape-5" />
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_magzine-maker/artifacts/c7i8lwjh_image.png" 
              alt="Treasure Cove Logo" 
              className="h-12 object-contain"
            />
            <h1 className="font-serif text-2xl text-[#0A192F] tracking-tight">
              Community Magazine
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-public"
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#4A5568] hover:text-[#0A192F] hover:bg-[#F4F1EA]"
            >
              <Home className="w-4 h-4 mr-2" />
              Public Site
            </Button>
            <Button
              data-testid="subscribers-btn"
              variant="outline"
              onClick={() => navigate('/admin/subscribers')}
              className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
            >
              <Users className="w-4 h-4 mr-2" />
              Subscribers
              {subscriberCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-[#40E0D0] text-white rounded-full">
                  {subscriberCount}
                </span>
              )}
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="ghost"
              onClick={() => { localStorage.removeItem('admin_token'); navigate('/admin'); }}
              className="text-[#4A5568] hover:text-[#FF6F61] hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-edition-btn"
                className="btn-coral flex items-center gap-2"
                onClick={handleOpenCreate}
              >
                <Plus className="w-5 h-5" />
                New Edition
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl p-8 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#0A192F]">
                  {createStep === 1 ? 'Choose a Template' : 'Create New Edition'}
                </DialogTitle>
              </DialogHeader>
              
              {createStep === 1 ? (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        data-testid={`template-${template.id}`}
                        onClick={() => setNewMagazine({ ...newMagazine, template: template.id })}
                        className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                          newMagazine.template === template.id 
                            ? 'border-[#FF6F61] shadow-lg' 
                            : 'border-[#E8E2D2] hover:border-[#FF6F61]/50'
                        }`}
                      >
                        <img 
                          src={template.preview_image} 
                          alt={template.name}
                          className="w-full h-32 object-cover"
                        />
                        <div className="p-3 bg-white">
                          <h4 className="font-medium text-[#0A192F] flex items-center gap-2">
                            {template.name}
                            {newMagazine.template === template.id && (
                              <Check className="w-4 h-4 text-[#FF6F61]" />
                            )}
                          </h4>
                          <p className="text-xs text-[#4A5568] mt-1">{template.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    data-testid="next-step-btn"
                    onClick={() => setCreateStep(2)}
                    className="btn-coral w-full mt-4"
                  >
                    Continue with {templates.find(t => t.id === newMagazine.template)?.name || 'Classic'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 mt-4">
                  <div className="flex items-center gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                    <Layout className="w-5 h-5 text-[#FF6F61]" />
                    <span className="text-sm text-[#4A5568]">
                      Template: <span className="font-medium text-[#0A192F]">
                        {templates.find(t => t.id === newMagazine.template)?.name || 'Classic'}
                      </span>
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setCreateStep(1)}
                      className="ml-auto text-[#FF6F61] hover:text-[#FF6F61]/80"
                    >
                      Change
                    </Button>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#4A5568] block mb-2">
                      Edition Title
                    </label>
                    <Input
                      data-testid="magazine-title-input"
                      placeholder="e.g., Summer 2024 Edition"
                      value={newMagazine.title}
                      onChange={(e) => setNewMagazine({ ...newMagazine, title: e.target.value })}
                      className="input-bahamian w-full"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#4A5568] block mb-2">
                      Description (optional)
                    </label>
                    <Textarea
                      data-testid="magazine-description-input"
                      placeholder="Brief description of this edition..."
                      value={newMagazine.description}
                      onChange={(e) => setNewMagazine({ ...newMagazine, description: e.target.value })}
                      className="input-bahamian w-full min-h-[100px] resize-none"
                    />
                  </div>
                  <Button
                    data-testid="create-magazine-submit-btn"
                    onClick={handleCreateMagazine}
                    disabled={creating}
                    className="btn-coral w-full mt-4"
                  >
                    {creating ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner" />
                        Creating...
                      </span>
                    ) : (
                      'Create Edition'
                    )}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-12 animate-fade-in relative">
          {/* Decorative circles - Bahamian colors */}
          <div className="absolute -top-10 left-1/4 w-40 h-40 bg-gradient-to-br from-[#40E0D0]/15 to-transparent rounded-full blur-2xl" />
          <div className="absolute -top-5 right-1/4 w-32 h-32 bg-gradient-to-br from-[#FF7F50]/12 to-transparent rounded-full blur-2xl" />
          <div className="absolute top-20 right-1/3 w-20 h-20 bg-gradient-to-br from-[#FFB347]/20 to-transparent rounded-full blur-xl" />
          <div className="absolute top-10 left-1/3 w-24 h-24 bg-gradient-to-br from-[#FC8EAC]/10 to-transparent rounded-full blur-xl" />
          
          <span className="text-xs tracking-[0.2em] uppercase font-medium text-[#FF7F50] mb-4 block relative z-10">
            Welcome to Your Magazine Studio
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#0A192F] tracking-tight mb-4 relative z-10">
            Create Beautiful Community Magazines
          </h2>
          <p className="text-[#4A5568] max-w-2xl mx-auto text-lg relative z-10">
            Design, write, and share professional magazines with the help of AI.
            Perfect for your Treasure Cove community stories.
          </p>
        </div>

        {/* Magazine Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="spinner" style={{ width: '40px', height: '40px' }} />
          </div>
        ) : magazines.length === 0 ? (
          <div className="card-ambient p-12 text-center animate-slide-up">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/e7f52e0f-cf36-4e96-9be9-66fd25742738/images/c879c503411b6a07a11f6f41bcdf88868f6d2bbf6f88e68f577bb03bee2358b5.png"
              alt="Create your first magazine"
              className="w-64 h-64 object-cover mx-auto rounded-2xl mb-6 shadow-lg"
            />
            <h3 className="font-serif text-2xl text-[#0A192F] mb-3">
              Start Your First Edition
            </h3>
            <p className="text-[#4A5568] mb-6 max-w-md mx-auto">
              Click "New Edition" to create your first magazine. Add sections, write articles, and share with your community.
            </p>
            <Button
              data-testid="empty-state-create-btn"
              onClick={handleOpenCreate}
              className="btn-coral"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Magazine
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {magazines.map((magazine) => (
              <div
                key={magazine.id}
                data-testid={`magazine-card-${magazine.id}`}
                className="card-ambient overflow-hidden cursor-pointer group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                onClick={() => navigate(`/admin/editor/${magazine.id}`)}
              >
                <div className="aspect-[4/3] bg-[#F4F1EA] relative overflow-hidden">
                  {magazine.cover_image ? (
                    <img
                      src={magazine.cover_image}
                      alt={magazine.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-[#E8E2D2]" strokeWidth={1} />
                    </div>
                  )}
                  {magazine.published && (
                    <span className="absolute top-3 left-3 bg-[#FF6F61] text-white text-xs px-3 py-1 rounded-full font-medium">
                      Published
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-xl text-[#0A192F] truncate mb-1">
                        {magazine.title}
                      </h3>
                      <p className="text-sm text-[#4A5568] line-clamp-2">
                        {magazine.description || 'No description'}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#4A5568]">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white border-[#E8E2D2]">
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/editor/${magazine.id}`); }}
                          className="cursor-pointer"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); navigate(`/view/${magazine.id}`); }}
                          className="cursor-pointer"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => handleDeleteMagazine(magazine.id, e)}
                          className="cursor-pointer text-red-500 focus:text-red-500"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2 mt-4 text-xs text-[#4A5568]">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{formatDate(magazine.created_at)}</span>
                    <span className="text-[#E8E2D2]">•</span>
                    <span>{magazine.sections?.length || 0} sections</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <CenturionFooter />
    </div>
  );
};

export default Dashboard;
